import type { Casework } from "building-types/casework/caseworkTypes";
import { CaseworkTypeName } from "building-types/casework/caseworkTypesEnum";
import type { Level } from "building-types/levels/levelTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallStrip } from "building-types/walls/wallStrip";
import * as constants from "common/constants/buildingObjects";
import uid from "common/utils/uid";
import { makeXYPlane } from "doc/measurementUtils";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { aabb3Center } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Extrusion, Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { extrudeFaceAlongNormal, extrudePolyLine2InZ, extrusionToBRep } from "geom/extrude";
import { geomMultiAABB3 } from "geom/geom";
import { line2Eval } from "geom/line2";
import { offsetPolyLine2, polyLine2ThickenedEdge } from "geom/offset";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeLength,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { rect2Extend, rect2MultiSplitFlex } from "geom/rect2";
import type { Vec3 } from "geom/vec3";
import { caseworkLocalToWorldTransformFromHostPath } from "./caseworkLocalTransform";
import { extrudeRectAlongPath } from "./caseworkUtils";

export function caseworkNew({
  id = uid(),
  room,
  planSolid = [],
  planDash = [],
  planPoche = [],
  planHidden = [],
  footprintOutlines = [],
  wallStripPath,
  depth,
  aabb3,
  caseworkMeasures,
  breps = [],
  wallSweepExtrusions = [],
  decorativeExtrusions = [],
  countertopExtrusions = [],
  decorativeBReps = [],
  planNotes = [],
  handle,
  handleAuxiliary,
  name = "",
  material = "propDefault0",
  countertopMaterial = "propDefault0",
  floorMaterial,
  entityInstanceType,
  closetShelfRodLines = [],
  paletteType = PaletteType.INTERIOR_PALETTE,
  tag,
  tagPlacements,
  roomEdgeIds = [],
  localToWorldTransform,
}: Partial<Casework> & Pick<Casework, "room">): Casework {
  return {
    ...buildingEntityBaseNew("CASEWORK", id, handle, entityInstanceType),
    room,
    planSolid,
    planDash,
    planPoche,
    planHidden,
    footprintOutlines,
    aabb3,
    wallStripPath,
    depth,
    breps,
    caseworkMeasures,
    wallSweepExtrusions,
    decorativeExtrusions,
    countertopExtrusions,
    decorativeBReps,
    planNotes,
    handle,
    handleAuxiliary,
    name,
    material,
    countertopMaterial,
    floorMaterial,
    closetShelfRodLines,
    paletteType,
    tag,
    roomEdgeIds,
    tagPlacements,
    localToWorldTransform,
    ...(room.instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: room.instanceId },
        }
      : {}),
  };
}

export function caseworkAABB3(casework: Casework): AABB3 {
  return geomMultiAABB3([
    ...casework.decorativeExtrusions,
    ...casework.breps,
    ...casework.countertopExtrusions,
    ...casework.wallSweepExtrusions,
  ]);
}

export function caseworkCentroid(c: Casework): Vec3 {
  const caseworkCentroid = caseworkAABB3(c);
  return aabb3Center(caseworkCentroid);
}

export function walkinShowerFromLine(
  room: Room,
  line: PolyLine2,
  start: number,
  end: number,
  width: number = 30,
): Casework {
  const edge = line;
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  const showerRect = polyLine2ThickenedEdge(edgeCopy, 0, -width);
  const showerDoorLine = polyLine2Edge(showerRect, 2);
  polyLine2ExtendStart(showerDoorLine, -1);
  const wall1 = polyLine2ThickenedEdge(showerDoorLine, 0, 0.5);
  const wall2 = polyLine2ThickenedEdge(showerRect, 1, 5);

  const cabExt = extrudePolyLine2InZ(wall1, room.level.floorElevation, 84);
  const cabExt1 = extrudePolyLine2InZ(wall2, room.level.floorElevation, 84);
  const floorExt = extrudePolyLine2InZ(showerRect, room.level.floorElevation, 1);

  const crossLine1 = polyLine2New([showerRect[0], showerRect[1], showerRect[4], showerRect[5]]);
  const crossLine2 = polyLine2New([showerRect[2], showerRect[3], showerRect[6], showerRect[7]]);

  const planSolid = [showerRect, crossLine1, crossLine2];

  return caseworkNew({
    room,
    planSolid,
    planDash: [],
    planPoche: [],
    planHidden: [],
    breps: [],
    decorativeExtrusions: [cabExt, cabExt1, floorExt],
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, [cabExt, cabExt1, floorExt]),
    planNotes: [],
    handle: undefined,
    name: CaseworkTypeName.WALK_IN_SHOWER,
  });
}

export function waterClosetFromLine2(
  room: Room,
  line: PolyLine2,
  z: number,
  height: number,
  start: number,
  end: number,
  width: number = 30,
): Casework {
  const edge = line;
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  const wcRect = polyLine2ThickenedEdge(edgeCopy, 0, -width);
  const wcDoorLine = polyLine2Edge(wcRect, 2);
  polyLine2ExtendStart(wcDoorLine, -1);
  const wall1 = polyLine2ThickenedEdge(wcDoorLine, 0, 5);
  const wall2 = polyLine2ThickenedEdge(wcRect, 1, 5);

  const wallExt = extrudePolyLine2InZ(wall1, z, height);
  const wallExt1 = extrudePolyLine2InZ(wall2, z, height);

  const planSolid = [wall1, wall2];

  return caseworkNew({
    room,
    planSolid,
    decorativeExtrusions: [wallExt, wallExt1],
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, [wallExt, wallExt1]),
  });
}

export function cabinetEndPanelFromLine(room: Room, line: PolyLine2, start: number): Casework {
  const edge = line;
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, 0);

  const outline = polyLine2ThickenedEdge(edgeCopy, 0, -constants.COUNTER_DEPTH);
  const panel = polyLine2ThickenedEdge(outline, 1, 0.75);

  const wallExt = extrudePolyLine2InZ(panel, room.level.floorElevation, 90);

  const planSolid = [panel];

  const wc = caseworkNew({
    room,
    planSolid,
    decorativeExtrusions: [wallExt],
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, [wallExt]),
    name: CaseworkTypeName.CABINET_END_PANEL,
  });
  return wc;
}

export function caseworkOverrideName(casework: Casework, name: string): Casework {
  return { ...casework, name };
}

export function cabinetFromLine(
  room: Room,
  edge: PolyLine2,
  start: number,
  end: number,
  offset: number = 0,
  upper: boolean = false,
  range: boolean = false,
  deep: boolean = false,
  seating: boolean = false,
  sinkPos: number = 0.5,
  tallUpper: boolean = false,
): Casework {
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  let depth = constants.COUNTER_DEPTH;
  const height = constants.COUNTER_HEIGHT;

  const offsetZone = polyLine2ThickenedEdge(edgeCopy, 0, -offset);

  const casework: Casework[] = [];

  if (offset > 24) {
    let cabinetRect;
    depth = constants.ISLAND_DEPTH;

    const decorativeExtrusions: Extrusion[] = [];

    let brace = polyLine2New([]);

    if (seating) {
      cabinetRect = polyLine2ThickenedEdge(offsetZone, 2, -depth + 12);
      const counterRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);
      rect2Extend(counterRect, 0, 24);
      brace = polyLine2ThickenedEdge(counterRect, 0, 3.5);
      decorativeExtrusions.push(extrudePolyLine2InZ(brace, room.level.floorElevation, height));
      const extension = polyLine2ThickenedEdge(counterRect, 0, 19);
      decorativeExtrusions.push(extrudePolyLine2InZ(extension, height - 1, 1));
    } else {
      cabinetRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);
    }

    const leftLine = polyLine2Edge(cabinetRect, 0);
    const rightLine = polyLine2Edge(cabinetRect, 2);

    const [x0, y0] = line2Eval(leftLine[0], leftLine[1], leftLine[2], leftLine[3], sinkPos, true);
    const [x1, y1] = line2Eval(rightLine[0], rightLine[1], rightLine[2], rightLine[3], 1 - sinkPos, true);

    const midLine = polyLine2ByTwoPoints(x0, y0, x1, y1);

    // make sink hole
    const sinkWidth = 42;
    const sinkRect = polyLine2ThickenedEdge(midLine, 0, sinkWidth / 2);
    rect2Extend(sinkRect, 0, sinkWidth / 2);
    rect2Extend(sinkRect, 1, -3);
    rect2Extend(sinkRect, 3, seating ? -2 : -12);

    const face = faceNew([cabinetRect, sinkRect], makeXYPlane(0, 0, room.level.floorElevation));

    const cabExt = extrudeFaceAlongNormal(face, constants.COUNTER_HEIGHT);
    decorativeExtrusions.push(cabExt);

    const cabBrep = extrusionToBRep(cabExt);
    const leftFace = cabBrep.faces[2];
    const rightFace = cabBrep.faces[4];

    const faces = [leftFace, rightFace];

    decorativeExtrusions.push(...cabinetDoorsFromFaces(faces, 14));

    const planSolid = [cabinetRect];
    const planDash = [brace];

    const cabinet = caseworkNew({
      room,
      planSolid,
      planDash,
      planPoche: [],
      planHidden: [],
      breps: [],
      decorativeExtrusions,
      localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, decorativeExtrusions),
      planNotes: [],
      handle: undefined,
      name: CaseworkTypeName.CABINET,
    });

    casework.push(cabinet);

    return cabinet;
  } else {
    let cabExt;
    let cabinetRect;

    if (upper) {
      if (!deep) {
        depth = constants.UPPER_CABINET_DEPTH;
      } else {
        depth = constants.COUNTER_DEPTH;
      }
      cabinetRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);

      const baseHeight = range
        ? room.level.floorElevation + constants.UPPER_BASE_ABOVE_RANGE
        : room.level.floorElevation + constants.UPPER_CABINET_BASE;
      let cabHeight = range ? constants.COUNTER_HEIGHT - 12 : constants.COUNTER_HEIGHT;

      if (tallUpper) {
        cabHeight += 6;
      }

      cabExt = extrudePolyLine2InZ(cabinetRect, baseHeight, cabHeight);
    } else {
      cabinetRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);
      cabExt = extrudePolyLine2InZ(cabinetRect, room.level.floorElevation, constants.COUNTER_HEIGHT);
    }

    const decorativeExtrusions: Extrusion[] = [];
    decorativeExtrusions.push(cabExt);

    const cabBrep = extrusionToBRep(cabExt);
    const leftFace = cabBrep.faces[4];

    const faces = [leftFace];

    const planLines = [cabinetRect];

    decorativeExtrusions.push(...cabinetDoorsFromFaces(faces));
    let cabinet;
    if (upper) {
      cabinet = caseworkNew({
        room,
        planDash: planLines,
        decorativeExtrusions,
        localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, decorativeExtrusions),
        handle: undefined,
        name: CaseworkTypeName.CABINET,
      });
    } else {
      cabinet = caseworkNew({
        room,
        planSolid: planLines,
        decorativeExtrusions,
        localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, decorativeExtrusions),
        handle: undefined,
        name: CaseworkTypeName.CABINET,
      });
    }
    return cabinet;
  }
}

export function vanityFromLine(room: Room, edge: PolyLine2, start: number, end: number, offset: number = 0): Casework {
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  const depth = constants.VANITY_DEPTH;

  const offsetZone = polyLine2ThickenedEdge(edgeCopy, 0, -offset);

  const decorativeExtrusions: Extrusion[] = [];

  const cabinetRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);

  const face = faceNew([cabinetRect], makeXYPlane(0, 0, room.level.floorElevation));

  const cabExt = extrudeFaceAlongNormal(face, constants.COUNTER_HEIGHT);
  decorativeExtrusions.push(cabExt);

  const cabBrep = extrusionToBRep(cabExt);
  const leftFace = cabBrep.faces[2];
  const rightFace = cabBrep.faces[4];

  const faces = [leftFace, rightFace];

  decorativeExtrusions.push(...cabinetDoorsFromFaces(faces));

  const planSolid = [cabinetRect];

  const cabinet = caseworkNew({
    room,
    planSolid,
    planDash: [],
    planPoche: [],
    planHidden: [],
    breps: [],
    decorativeExtrusions,
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, decorativeExtrusions),
    planNotes: [],
    handle: undefined,
    name: CaseworkTypeName.VANITY,
  });

  return cabinet;
}

export function chaseFromLine(room: Room, edge: PolyLine2, start: number, end: number, depth: number): Casework {
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  const cabinetRect = polyLine2ThickenedEdge(edgeCopy, 0, -depth);
  const cabExt = extrudePolyLine2InZ(cabinetRect, room.level.floorElevation, room.level.topOfWall);

  const planSolid = [cabinetRect];

  const cabinet = caseworkNew({
    room,
    planSolid,
    decorativeExtrusions: [cabExt],
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(edgeCopy, [cabExt]),
    name: CaseworkTypeName.CHASE,
  });
  return cabinet;
}

export function soffitFromLine(room: Room, edge: PolyLine2, start: number, end: number, offset: number = 0): Casework {
  const edgeCopy = polyLine2Copy(edge);

  polyLine2ExtendStart(edgeCopy, -start);
  polyLine2ExtendEnd(edgeCopy, -end);

  const depth = constants.WALL_THICKNESS_INTERIOR;

  const offsetZone = polyLine2ThickenedEdge(edgeCopy, 0, -offset);
  const benchRect = polyLine2ThickenedEdge(offsetZone, 2, -depth);

  const planDash = [benchRect];

  const soffit = caseworkNew({
    room,
    planDash,
    name: CaseworkTypeName.SOFFIT,
  });
  return soffit;
}

export function shelfAlongWallStrip(strip: WallStrip, level: Level, paramStart?: number, paramEnd?: number) {
  const stripPath = strip.baselineMaxOffset(true);
  const shelfExt = extrudeRectAlongPath({
    stripPath,
    baseHeight: level.floorElevation + constants.SHELF_HEIGHT,
    thickness: constants.SHELF_THICKNESS,
    depth: constants.SHELF_DEPTH,
    paramStart,
    paramEnd,
  });

  const shelfOutline = shelfExt.profile.loops[0];

  const showerRod = offsetPolyLine2(strip.baseline(true), -11);

  const planSolid = [shelfOutline];

  const planDash = [showerRod];

  return caseworkNew({
    room: strip.room,
    planSolid,
    planDash,
    decorativeExtrusions: [shelfExt],
    localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(stripPath, [shelfExt]),
    name: CaseworkTypeName.SHELF,
  });
}

function cabinetDoorsFromFaces(
  faces: Face[],
  width: number = 14,
  doorInset: number = 2,
  doorThickness: number = 1,
): Extrusion[] {
  const decorativeExtrusions: Extrusion[] = [];
  for (const face of faces) {
    const cabLen = polyLine2EdgeLength(face.loops[0], 1);
    const divs: number[] = [];
    const divNum = Math.floor(cabLen / width);
    for (let i = 0; i < divNum; i++) {
      divs.push(0);
    }
    const cabDoors = rect2MultiSplitFlex(face.loops[0], divs, false);
    for (const door of cabDoors) {
      const doorOffset = offsetPolyLine2(door, doorInset);
      const doorFace = faceNew([doorOffset], face.plane);
      const doorExt = extrudeFaceAlongNormal(doorFace, doorThickness);
      decorativeExtrusions.push(doorExt);
    }
  }
  return decorativeExtrusions;
}
